import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  Inbox,
  RefreshCw,
  LogIn,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Button, Input, Textarea, Select, SelectItem, Spinner } from '@nextui-org/react';
import { useAuth } from '../contexts/AuthContext';
import contactRequestService, {
  CATEGORY_OPTIONS,
  ContactRequest,
  categoryLabel,
  statusLabel,
} from '../services/contactRequestService';
import ContactRequestDetailModal from '../components/contact/ContactRequestDetailModal';

const ACCENT = '#3fced1';

const relativeTime = (ts?: number | null): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (day === today) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (day === today - 86400000) return 'Gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [activeRequest, setActiveRequest] = useState<ContactRequest | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'requests'>('form');

  const loadRequests = useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingRequests(false);
      return;
    }
    setLoadingRequests(true);
    const list = await contactRequestService.getMyRequests();
    setRequests(list);
    setLoadingRequests(false);
  }, [isAuthenticated]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const validate = (): string | null => {
    if (subject.trim().length < 3) return 'Betreff muss mindestens 3 Zeichen lang sein.';
    if (messageText.trim().length < 10) return 'Nachricht muss mindestens 10 Zeichen lang sein.';
    return null;
  };

  const handleSubmit = async () => {
    setFeedback(null);
    const err = validate();
    if (err) {
      setFeedback({ type: 'error', text: err });
      return;
    }
    setSending(true);
    try {
      const created = await contactRequestService.createRequest({
        category,
        subject: subject.trim(),
        message: messageText.trim(),
      });
      if (created) {
        setSubject('');
        setMessageText('');
        setCategory('general');
        setFeedback({ type: 'success', text: 'Anfrage erfolgreich gesendet!' });
        await loadRequests();
        setActiveTab('requests');
      } else {
        setFeedback({ type: 'error', text: 'Ein Fehler ist aufgetreten.' });
      }
    } catch (e: any) {
      const status = e?.response?.status;
      setFeedback({
        type: 'error',
        text:
          status === 401
            ? 'Bitte melde dich an, um eine Anfrage zu senden.'
            : 'Die Anfrage konnte nicht gesendet werden.',
      });
    } finally {
      setSending(false);
    }
  };

  const onRequestUpdated = (updated: ContactRequest) => {
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setActiveRequest((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  // -------------------------------------------------------------------------

  const renderForm = () => (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
      <h2 className="mb-5 text-xl font-bold text-white">Neue Anfrage</h2>
      <div className="space-y-5">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-300">Kategorie</label>
          <Select
            aria-label="Kategorie"
            selectedKeys={[category]}
            onChange={(e) => setCategory(e.target.value || 'general')}
            isDisabled={sending}
            classNames={{
              trigger: 'bg-zinc-800 border border-zinc-700 data-[hover=true]:bg-zinc-800',
              popoverContent: 'bg-zinc-900',
            }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value}>{opt.label}</SelectItem>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-300">Betreff</label>
          <Input
            aria-label="Betreff"
            placeholder="Worum geht es?"
            value={subject}
            onValueChange={setSubject}
            isDisabled={sending}
            classNames={{
              inputWrapper: 'bg-zinc-800 border border-zinc-700 data-[hover=true]:bg-zinc-800',
            }}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-300">Nachricht</label>
          <Textarea
            aria-label="Nachricht"
            placeholder="Beschreibe dein Anliegen..."
            minRows={6}
            value={messageText}
            onValueChange={setMessageText}
            isDisabled={sending}
            classNames={{
              inputWrapper: 'bg-zinc-800 border border-zinc-700 data-[hover=true]:bg-zinc-800',
            }}
          />
        </div>

        {feedback && (
          <div
            className={`flex items-center gap-2 rounded-xl border p-3 text-sm ${
              feedback.type === 'success'
                ? 'border-emerald-800 bg-emerald-950/40 text-emerald-300'
                : 'border-red-900 bg-red-950/40 text-red-300'
            }`}
          >
            {feedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {feedback.text}
          </div>
        )}

        <Button
          onPress={handleSubmit}
          isLoading={sending}
          startContent={!sending ? <Send size={18} /> : undefined}
          className="w-full bg-[#3fced1] py-6 text-base font-semibold text-black"
        >
          Anfrage senden
        </Button>
      </div>
    </div>
  );

  const renderRequests = () => (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Meine Anfragen</h2>
        {!loadingRequests && (
          <button
            type="button"
            onClick={loadRequests}
            aria-label="Aktualisieren"
            className="text-gray-400 transition-colors hover:text-white"
          >
            <RefreshCw size={18} />
          </button>
        )}
      </div>

      {loadingRequests ? (
        <div className="flex justify-center py-10">
          <Spinner color="primary" />
        </div>
      ) : requests.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Inbox size={44} className="text-zinc-700" />
          <p className="text-sm text-gray-400">Noch keine Anfragen</p>
          <p className="text-xs text-gray-600">Deine Anfragen erscheinen hier.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <RequestCard key={r.id} request={r} onClick={() => setActiveRequest(r)} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950">
      <Helmet>
        <title>Kontakt | DartCloud</title>
        <meta name="description" content="Kontaktiere das DartCloud-Team und verfolge deine Anfragen." />
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {/* Header */}
        <h1 className="text-3xl font-extrabold text-white sm:text-4xl">Kontakt</h1>
        <p className="mt-2 text-gray-400">Hast du Fragen oder Anregungen? Schreib uns!</p>

        <div className="mt-8">
          {!isAuthenticated ? (
            <div className="flex flex-col items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-10 text-center">
              <LogIn size={56} className="text-zinc-600" />
              <div>
                <h3 className="text-xl font-semibold text-white">Anmeldung erforderlich</h3>
                <p className="mt-1 text-sm text-gray-400">Bitte melde dich an, um uns zu kontaktieren.</p>
              </div>
              <Button
                onPress={() => navigate('/login')}
                className="bg-[#3fced1] font-semibold text-black"
              >
                Jetzt anmelden
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Tabs */}
              <div className="mb-6 flex gap-1 rounded-xl border border-zinc-800 bg-zinc-900 p-1 lg:hidden">
                {(['form', 'requests'] as const).map((tab) => {
                  const active = activeTab === tab;
                  const unread = requests.reduce((s, r) => s + r.unreadCountUser, 0);
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        active ? 'text-black' : 'text-gray-400'
                      }`}
                      style={active ? { backgroundColor: ACCENT } : undefined}
                    >
                      {tab === 'form' ? 'Formular' : 'Meine Anfragen'}
                      {tab === 'requests' && unread > 0 && (
                        <span className="rounded-full bg-black/20 px-1.5 text-[11px] font-bold">
                          {unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Desktop: zwei Spalten */}
              <div className="hidden gap-6 lg:grid lg:grid-cols-[3fr_2fr]">
                {renderForm()}
                {renderRequests()}
              </div>

              {/* Mobile: aktiver Tab */}
              <div className="lg:hidden">
                {activeTab === 'form' ? renderForm() : renderRequests()}
              </div>
            </>
          )}
        </div>
      </div>

      {activeRequest && (
        <ContactRequestDetailModal
          request={activeRequest}
          isOpen={!!activeRequest}
          onClose={() => setActiveRequest(null)}
          onRequestUpdated={onRequestUpdated}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

const RequestCard: React.FC<{ request: ContactRequest; onClick: () => void }> = ({
  request,
  onClick,
}) => {
  const hasUnread = request.unreadCountUser > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border bg-zinc-900 p-4 text-left transition-colors hover:bg-zinc-800 ${
        hasUnread ? 'border-[#3fced1]' : 'border-zinc-800'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="line-clamp-1 flex-1 text-sm font-semibold text-white">
          {request.subject}
        </span>
        {hasUnread && (
          <span
            className="rounded-full px-1.5 text-[11px] font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            {request.unreadCountUser}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StatusBadge status={request.status} />
        <span className="rounded-md bg-zinc-800 px-2 py-0.5 text-[11px] text-gray-400">
          {categoryLabel(request.category)}
        </span>
        <span className="ml-auto text-[11px] text-gray-500">
          {relativeTime(request.lastMessageTime ?? request.createdAt)}
        </span>
      </div>
      {request.lastMessage && (
        <p className="mt-2 line-clamp-2 text-xs text-gray-400">{request.lastMessage}</p>
      )}
    </button>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
    style={{ backgroundColor: 'rgba(63,206,209,0.18)', color: ACCENT }}
  >
    {statusLabel(status)}
  </span>
);

export default ContactPage;
